import { useEffect, useMemo, useState } from "react";
import { CalendarDays } from "lucide-react";
import { isAfter, startOfDay } from "date-fns";
import type { DateRange } from "react-day-picker";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as DateRangeCalendar } from "@/components/ui/calendar";
import { formatDateMdy } from "@/components/customeDatepicker";
import { getReportDateRange } from "@/utils/helpers";

export function useNursingNotesDateRange() {
  const [nursingMode, setNursingMode] = useState<"month" | "custom">("month");
  const [nursingSelectedMonth, setNursingSelectedMonth] = useState(() =>
    String(new Date().getMonth() + 1).padStart(2, "0"),
  );
  const [nursingSelectedYear, setNursingSelectedYear] = useState(() =>
    String(new Date().getFullYear()),
  );
  const nursingSelectedMonthKey = useMemo(
    () => `${nursingSelectedYear}-${nursingSelectedMonth}`,
    [nursingSelectedYear, nursingSelectedMonth],
  );

  const nursingMonthOptions = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(undefined, { month: "long" });
    return Array.from({ length: 12 }, (_, i) => {
      const m = String(i + 1).padStart(2, "0");
      const label = fmt.format(new Date(2020, i, 1));
      return { key: m, label };
    });
  }, []);

  const nursingYearOptions = useMemo(() => {
    const nowY = new Date().getFullYear();
    const out: { key: string; label: string }[] = [];
    for (let y = nowY; y >= nowY - 9; y--) {
      out.push({ key: String(y), label: String(y) });
    }
    return out;
  }, []);

  const [nursingCustomRange, setNursingCustomRange] = useState<DateRange | undefined>();
  const [draftNursingCustomRange, setDraftNursingCustomRange] = useState<DateRange | undefined>();
  const [nursingCustomPickerOpen, setNursingCustomPickerOpen] = useState(false);
  const [nursingActiveRangeBoundary, setNursingActiveRangeBoundary] = useState<
    "from" | "to" | null
  >(null);

  const isFutureCalendarDay = (d: Date) =>
    isAfter(startOfDay(d), startOfDay(new Date()));

  const handleNursingCustomRangeSelect = (_next: DateRange | undefined, day?: Date) => {
    if (!day || isFutureCalendarDay(day)) return;

    setDraftNursingCustomRange((prev) => {
      const from = prev?.from;
      const to = prev?.to;

      if (!from || !to) {
        if (from && !to) {
          setNursingActiveRangeBoundary(null);
          return day < from ? { from: day, to: from } : { from, to: day };
        }
        setNursingActiveRangeBoundary("to");
        return { from: day, to: undefined };
      }

      if (day.toDateString() === from.toDateString()) {
        setNursingActiveRangeBoundary("from");
        return prev;
      }
      if (day.toDateString() === to.toDateString()) {
        setNursingActiveRangeBoundary("to");
        return prev;
      }

      if (nursingActiveRangeBoundary === "from") {
        setNursingActiveRangeBoundary(null);
        return day <= to ? { from: day, to } : { from: to, to: day };
      }
      if (nursingActiveRangeBoundary === "to") {
        setNursingActiveRangeBoundary(null);
        return day >= from ? { from, to: day } : { from: day, to: from };
      }

      setNursingActiveRangeBoundary("to");
      return { from: day, to: undefined };
    });
  };

  const { startDate: nursingStartDate, endDate: nursingEndDate } = useMemo(() => {
    if (nursingMode === "custom") {
      return getReportDateRange(
        "custom",
        nursingCustomRange?.from,
        nursingCustomRange?.to ?? nursingCustomRange?.from,
      );
    }
    const [yStr, mStr] = String(nursingSelectedMonthKey).split("-");
    const y = Number(yStr);
    const m = Number(mStr);
    if (!y || !m) return getReportDateRange("last30");
    const from = new Date(y, m - 1, 1);
    const to = new Date(y, m, 0);
    return getReportDateRange("custom", from, to);
  }, [nursingMode, nursingCustomRange, nursingSelectedMonthKey]);

  const [nursingFilters, setNursingFilters] = useState(() => ({
    startDate: nursingStartDate,
    endDate: nursingEndDate,
  }));

  useEffect(() => {
    setNursingFilters({ startDate: nursingStartDate, endDate: nursingEndDate });
  }, [nursingStartDate, nursingEndDate]);

  const handleNursingCustomPickerOpenChange = (open: boolean) => {
    setNursingCustomPickerOpen(open);
    if (open) {
      setDraftNursingCustomRange(nursingCustomRange);
      setNursingActiveRangeBoundary(null);
      return;
    }
    setNursingCustomRange(draftNursingCustomRange);
    setNursingActiveRangeBoundary(null);
  };

  const toMdy = (d?: Date) => (d ? formatDateMdy(d) : "");

  const toolbar = (
    <div className="flex shrink-0 flex-nowrap items-center gap-2">
      <div className="flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-1.5">
        <Select
          value={nursingMode}
          onValueChange={(v) => {
            const mode = v as "month" | "custom";
            setNursingMode(mode);
            if (mode === "custom") {
              handleNursingCustomPickerOpenChange(true);
            } else {
              setNursingCustomPickerOpen(false);
              setNursingActiveRangeBoundary(null);
            }
          }}
        >
          <SelectTrigger className="h-8 w-[min(130px,32vw)] border-0 px-1.5 shadow-none">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="month">Month</SelectItem>
            <SelectItem value="custom">Custom date range</SelectItem>
          </SelectContent>
        </Select>

        {nursingMode === "month" ? (
          <>
            <div className="mx-0.5 h-5 w-px bg-gray-200" />
            <Select value={nursingSelectedMonth} onValueChange={(v) => setNursingSelectedMonth(String(v))}>
              <SelectTrigger className="h-8 w-[min(120px,30vw)] border-0 px-1.5 shadow-none">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                {nursingMonthOptions.map((o) => (
                  <SelectItem key={o.key} value={o.key}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={nursingSelectedYear} onValueChange={(v) => setNursingSelectedYear(String(v))}>
              <SelectTrigger className="h-8 w-[min(96px,24vw)] border-0 px-1.5 shadow-none">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {nursingYearOptions.map((o) => (
                  <SelectItem key={o.key} value={o.key}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        ) : null}

        {nursingMode === "custom" ? (
          <>
            <div className="mx-0.5 h-5 w-px bg-gray-200" />
            <Popover open={nursingCustomPickerOpen} onOpenChange={handleNursingCustomPickerOpenChange}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-8 whitespace-nowrap rounded-md px-1.5 text-foreground hover:bg-gray-50 hover:text-foreground"
                >
                  <CalendarDays className="mr-2 h-4 w-4" />
                  {draftNursingCustomRange?.from && draftNursingCustomRange?.to
                    ? `${toMdy(draftNursingCustomRange.from)} — ${toMdy(draftNursingCustomRange.to)}`
                    : draftNursingCustomRange?.from
                      ? `${toMdy(draftNursingCustomRange.from)} — …`
                      : "Pick dates"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <DateRangeCalendar
                  mode="range"
                  numberOfMonths={2}
                  selected={draftNursingCustomRange}
                  onSelect={handleNursingCustomRangeSelect as (range?: DateRange, day?: Date) => void}
                  defaultMonth={draftNursingCustomRange?.from}
                  disabled={(date) => isFutureCalendarDay(date)}
                />
              </PopoverContent>
            </Popover>
          </>
        ) : null}
      </div>
    </div>
  );

  return {
    nursingFilters,
    setNursingFilters,
    nursingDateRangeToolbar: toolbar,
  };
}
